"use client"
import { useState, useEffect } from "react"
import { horseService } from "../services/api"
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Fab,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
} from "@mui/material"
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material"
import { toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { GiHorseHead } from "react-icons/gi"

export default function HorsePensionDetails({ userRole }) {
  const [openDialog, setOpenDialog] = useState(false)
  const [currentHorse, setCurrentHorse] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [horses, setHorses] = useState([])
  const [owners, setOwners] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [horseToDelete, setHorseToDelete] = useState(null)

  // Form state for adding/editing a horse
  const [formData, setFormData] = useState({
    name: "",
    user_id: "",
    groom: "",
    pension_type: "1",
    hg: "",
    pt: "",
    weight: "",
    ufc: "",
    last_deworming: "",
    vaccine_type: "",
    vaccine_date: "",
    vaccine_doctor: "",
    last_farrier_visit: "",
    marichal_name: "",
    bedding_type: "", // Kept for potential backward compatibility or if needed for single entry
    bedding_date: "", // Kept for potential backward compatibility or if needed for single entry
    food_supplement: "", // Kept for potential backward compatibility or if needed for single entry
    description: "",
    bedding_history: [],
    food_supplement_history: [],
  })

  // Fetch horses and owners on component mount
  useEffect(() => {
    fetchHorses()
    if (userRole === "admin") {
      fetchOwners()
    }
  }, [userRole])

  // Calculate weight based on HG and PT only if weight field is empty
  useEffect(() => {
    if (formData.hg && formData.pt && !formData.weight) {
      const hg = Number.parseFloat(formData.hg)
      const pt = Number.parseFloat(formData.pt)
      if (!isNaN(hg) && !isNaN(pt)) {
        const calculatedWeight = 3 * hg + 4.3 * pt - 785
        setFormData((prev) => ({
          ...prev,
          weight: calculatedWeight.toFixed(2),
        }))
      }
    }
  }, [formData.hg, formData.pt, formData.weight])

  // Fetch horses from API
  const fetchHorses = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await horseService.getAllHorses()
      if (response.data.status === "success") {
        setHorses(response.data.data)
      } else {
        setError("Failed to fetch horses data")
        toast.error("Failed to fetch horses data")
      }
    } catch (err) {
      console.error("Error fetching horses:", err)
      setError("Error connecting to the server")
      toast.error("Error connecting to the server")
    } finally {
      setLoading(false)
    }
  }

  // Fetch owners from API
  const fetchOwners = async () => {
    try {
      const response = await horseService.getHorseOwners()
      if (response.data.status === "success") {
        setOwners(response.data.data)
      } else {
        console.error("Failed to fetch owners")
      }
    } catch (err) {
      console.error("Error fetching owners:", err)
    }
  }

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  // Add bedding history entry
  const addBeddingEntry = () => {
    setFormData({
      ...formData,
      bedding_history: [...formData.bedding_history, { bedding_type: "", date: "" }],
    })
  }

  // Remove bedding history entry
  const removeBeddingEntry = (index) => {
    const newHistory = formData.bedding_history.filter((_, i) => i !== index)
    setFormData({
      ...formData,
      bedding_history: newHistory,
    })
  }

  // Update bedding history entry
  const updateBeddingEntry = (index, field, value) => {
    const newHistory = [...formData.bedding_history]
    newHistory[index][field] = value
    setFormData({
      ...formData,
      bedding_history: newHistory,
    })
  }

  // Add food supplement history entry
  const addFoodSupplementEntry = () => {
    setFormData({
      ...formData,
      food_supplement_history: [...formData.food_supplement_history, { description: "", date: "" }],
    })
  }

  // Remove food supplement history entry
  const removeFoodSupplementEntry = (index) => {
    const newHistory = formData.food_supplement_history.filter((_, i) => i !== index)
    setFormData({
      ...formData,
      food_supplement_history: newHistory,
    })
  }

  // Update food supplement history entry
  const updateFoodSupplementEntry = (index, field, value) => {
    const newHistory = [...formData.food_supplement_history]
    newHistory[index][field] = value
    setFormData({
      ...formData,
      food_supplement_history: newHistory,
    })
  }

  // Open dialog for adding a new horse
  const handleAddHorse = () => {
    setIsEditing(false)
    setCurrentHorse(null)
    setFormData({
      name: "",
      user_id: "",
      groom: "",
      pension_type: "1",
      hg: "",
      pt: "",
      weight: "",
      ufc: "",
      last_deworming: "",
      vaccine_type: "",
      vaccine_date: "",
      vaccine_doctor: "",
      last_farrier_visit: "",
      marichal_name: "",
      bedding_type: "",
      bedding_date: "",
      food_supplement: "",
      description: "",
      bedding_history: [],
      food_supplement_history: [],
    })
    setOpenDialog(true)
  }

  // Open dialog for editing an existing horse
  const handleEditHorse = (horse) => {
    setIsEditing(true)
    setCurrentHorse(horse)
    setFormData({
      name: horse.name,
      user_id: horse.user_id,
      groom: horse.groom,
      pension_type: horse.pension_type,
      hg: horse.hg || "",
      pt: horse.pt || "",
      weight: horse.weight,
      ufc: horse.ufc,
      last_deworming: horse.last_deworming,
      vaccine_type: horse.vaccine_type,
      vaccine_date: horse.vaccine_date,
      vaccine_doctor: horse.vaccine_doctor,
      last_farrier_visit: horse.last_farrier_visit,
      marichal_name: horse.marichal_name || "",
      bedding_type: horse.bedding_type, // Keep for potential backward compatibility
      bedding_date: horse.bedding_date, // Keep for potential backward compatibility
      food_supplement: horse.food_supplement, // Keep for potential backward compatibility
      description: horse.description || "",
      bedding_history: horse.bedding_history || [],
      food_supplement_history: horse.food_supplement_history || [],
    })
    setOpenDialog(true)
  }

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false)
  }

  // Update the handleSubmit function to handle direct weight changes without HG and PT
  const handleSubmit = async () => {
    try {
      let finalWeight = formData.weight
      let weightData = null

      // Check if we have a weight value (either direct or calculated)
      if (!finalWeight && (!formData.hg || !formData.pt)) {
        toast.error("Veuillez saisir soit le poids directement, soit les valeurs HG et PT pour le calcul automatique")
        return
      }

      // Prepare weight data for history tracking
      if (formData.hg && formData.pt) {
        // If HG and PT are provided, calculate weight and store all values
        const hg = Number.parseFloat(formData.hg)
        const pt = Number.parseFloat(formData.pt)
        const calculatedWeight = 3 * hg + 4.3 * pt - 785

        // Use calculated weight if no direct weight was entered
        if (!formData.weight) {
          finalWeight = calculatedWeight.toFixed(2)
        }

        weightData = {
          hg: formData.hg,
          pt: formData.pt,
          calculated_weight: calculatedWeight.toFixed(2),
          date: new Date().toISOString().split("T")[0], // Format YYYY-MM-DD
        }
      } else if (formData.weight) {
        // If only direct weight is provided (no HG/PT), store just the weight
        weightData = {
          hg: null,
          pt: null,
          calculated_weight: formData.weight,
          date: new Date().toISOString().split("T")[0],
        }
      }

      if (isEditing) {
        // Update existing horse
        const response = await horseService.updateHorse(currentHorse.id, {
          ...formData,
          weight: finalWeight,
        })

        if (response.data.status === "success") {
          // Add weight history record if weight has changed
          if (
            weightData &&
            (currentHorse.weight !== finalWeight || currentHorse.hg !== formData.hg || currentHorse.pt !== formData.pt)
          ) {
            await horseService.addHorseWeight(currentHorse.id, weightData)
          }

          toast.success("Cheval mis à jour avec succès!")
          fetchHorses()
        } else {
          toast.error("Erreur lors de la mise à jour du cheval")
        }
      } else {
        // Add new horse
        const horseDataWithWeight = {
          ...formData,
          weight: finalWeight,
        }

        const response = await horseService.createHorse(horseDataWithWeight)

        if (response.data.status === "success") {
          // Add weight history record
          if (weightData) {
            const horseId = response.data.data.id
            await horseService.addHorseWeight(horseId, weightData)
          }

          toast.success("Cheval ajouté avec succès!")
          fetchHorses()
        } else {
          toast.error("Erreur lors de l'ajout du cheval")
        }
      }

      setOpenDialog(false)
    } catch (err) {
      console.error("Error submitting form:", err)
      toast.error("Erreur de connexion au serveur")
    }
  }

  // Open delete confirmation dialog
  const handleDeleteHorse = (id) => {
    setHorseToDelete(id)
    setDeleteDialogOpen(true)
  }

  // Confirm and execute horse deletion
  const confirmDeleteHorse = async () => {
    try {
      const response = await horseService.deleteHorse(horseToDelete)
      if (response.data.status === "success") {
        toast.success("Cheval supprimé avec succès!")
        fetchHorses()
      } else {
        toast.error("Erreur lors de la suppression du cheval")
      }
    } catch (err) {
      console.error("Error deleting horse:", err)
      toast.error("Erreur de connexion au serveur")
    } finally {
      setDeleteDialogOpen(false)
      setHorseToDelete(null)
    }
  }

  // Cancel horse deletion
  const cancelDeleteHorse = () => {
    setDeleteDialogOpen(false)
    setHorseToDelete(null)
  }

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "-"

    try {
      // Handle ISO format or any other date string
      const date = new Date(dateString)

      // Check if date is valid
      if (isNaN(date.getTime())) {
        // If the date is not a valid Date object, try to extract from the string
        if (typeof dateString === "string") {
          // Try to extract DD/MM/YYYY from formats like "15T00:00:00.000000Z/11/2023"
          const parts = dateString.split("/")
          if (parts.length === 3) {
            // If it's already in DD/MM/YYYY format or similar with slashes
            return dateString
          }

          // Try to extract from other formats
          const matches = dateString.match(/(\d{1,2})[T\s].*\/(\d{1,2})\/(\d{4})/)
          if (matches && matches.length === 4) {
            return `${matches[1].padStart(2, "0")}/${matches[2].padStart(2, "0")}/${matches[3]}`
          }
        }
        return dateString // Return original if we can't parse it
      }

      // Format as DD/MM/YYYY
      const day = date.getDate().toString().padStart(2, "0")
      const month = (date.getMonth() + 1).toString().padStart(2, "0")
      const year = date.getFullYear()

      return `${day}/${month}/${year}`
    } catch (error) {
      console.error("Error formatting date:", error)
      return dateString // Return original on error
    }
  }

  return (
    <>
      {/* Horse Details Table */}
      <Paper elevation={2} sx={{ mb: 4, overflow: "hidden" }}>
        <Box
          sx={{
            p: 2,
            backgroundColor: "#003366",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <GiHorseHead style={{ fontSize: 28, marginRight: "16px" }} />
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Détails des Chevaux en Pension
            </Typography>
          </Box>
          <Box>
            {userRole === "admin" && (
              <Tooltip title="Ajouter un cheval">
                <Fab
                  size="small"
                  color="primary"
                  aria-label="add"
                  onClick={handleAddHorse}
                  sx={{ backgroundColor: "#b8860b", "&:hover": { backgroundColor: "#8B6914" } }}
                >
                  <AddIcon />
                </Fab>
              </Tooltip>
            )}
            <Tooltip title="Rafraîchir">
              <IconButton color="inherit" onClick={fetchHorses} sx={{ ml: 1 }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3, textAlign: "center", color: "error.main" }}>
            <Typography>{error}</Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchHorses}
              variant="contained"
              sx={{ mt: 2, backgroundColor: "#003366" }}
            >
              Réessayer
            </Button>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="horse details table">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#f0f0f0",
                      color: "#003366",
                      border: "1px solid #ddd",
                    }}
                  >
                    Nom du cheval
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#f0f0f0",
                      color: "#003366",
                      border: "1px solid #ddd",
                    }}
                  >
                    Propriétaire
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#f0f0f0",
                      color: "#003366",
                      border: "1px solid #ddd",
                    }}
                  >
                    Groom concerné
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#f0f0f0",
                      color: "#003366",
                      border: "1px solid #ddd",
                    }}
                  >
                    Type de pension
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#f0f0f0",
                      color: "#003366",
                      border: "1px solid #ddd",
                    }}
                  >
                    Poids en KG
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#f0f0f0",
                      color: "#003366",
                      border: "1px solid #ddd",
                    }}
                  >
                    UFC
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#f0f0f0",
                      color: "#003366",
                      border: "1px solid #ddd",
                    }}
                  >
                    Dernière vermifuge
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#f0f0f0",
                      color: "#003366",
                      border: "1px solid #ddd",
                    }}
                  >
                    Dernier Vaccin
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#f0f0f0",
                      color: "#003366",
                      border: "1px solid #ddd",
                    }}
                  >
                    Dernière visite du maréchal ferrant
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#f0f0f0",
                      color: "#003366",
                      border: "1px solid #ddd",
                    }}
                  >
                    Supp litière
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#f0f0f0",
                      color: "#003366",
                      border: "1px solid #ddd",
                    }}
                  >
                    Supp Alim/Vit
                  </TableCell>
                  {userRole === "admin" && (
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#f0f0f0",
                        color: "#003366",
                        border: "1px solid #ddd",
                      }}
                    >
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {horses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={userRole === "admin" ? 12 : 11} align="center">
                      Aucun cheval en pension pour le moment.
                    </TableCell>
                  </TableRow>
                ) : (
                  horses.map((horse, index) => (
                    <TableRow key={horse.id} sx={{ backgroundColor: index % 2 === 0 ? "#fff" : "#f9f9f9" }}>
                      <TableCell sx={{ fontWeight: "bold", border: "1px solid #ddd" }}>{horse.name}</TableCell>
                      <TableCell sx={{ border: "1px solid #ddd" }}>{horse.owner}</TableCell>
                      <TableCell sx={{ border: "1px solid #ddd" }}>{horse.groom}</TableCell>
                      <TableCell>
                        <Chip
                          label={`Pension ${horse.pension_type}`}
                          color={horse.pension_type === "1" ? "primary" : "secondary"}
                          size="small"
                          sx={{
                            backgroundColor: horse.pension_type === "1" ? "#003366" : "#b8860b",
                            color: "white",
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ border: "1px solid #ddd" }}>
                        {horse.weight ? `${Number.parseFloat(horse.weight).toFixed(2)} kg` : "-"}
                      </TableCell>
                      <TableCell sx={{ border: "1px solid #ddd" }}>{horse.ufc}</TableCell>
                      <TableCell sx={{ border: "1px solid #ddd" }}>
                        <Box>
                          {horse.description && (
                            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                              {horse.description}
                            </Typography>
                          )}
                          <Typography variant="body2">{formatDate(horse.last_deworming)}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ border: "1px solid #ddd" }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            {horse.vaccine_type || "-"}
                          </Typography>
                          <Typography variant="caption">{formatDate(horse.vaccine_date)}</Typography>
                          <Typography variant="caption" display="block" sx={{ color: "text.secondary" }}>
                            {horse.vaccine_doctor || "-"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ border: "1px solid #ddd" }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            {formatDate(horse.last_farrier_visit)}
                          </Typography>
                          {horse.marichal_name && (
                            <Typography variant="caption" display="block" sx={{ color: "text.secondary" }}>
                              {horse.marichal_name}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ border: "1px solid #ddd", maxWidth: "200px" }}>
                        {horse.bedding_history && horse.bedding_history.length > 0 ? (
                          <Box>
                            {horse.bedding_history.slice(0, 2).map((bedding, idx) => (
                              <Box key={idx} sx={{ mb: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                  {bedding.bedding_type}
                                </Typography>
                                <Typography variant="caption">{formatDate(bedding.date)}</Typography>
                              </Box>
                            ))}
                            {horse.bedding_history.length > 2 && (
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                +{horse.bedding_history.length - 2} autres
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell sx={{ border: "1px solid #ddd", maxWidth: "200px" }}>
                        {horse.food_supplement_history && horse.food_supplement_history.length > 0 ? (
                          <Box>
                            {horse.food_supplement_history.slice(0, 2).map((supplement, idx) => (
                              <Box key={idx} sx={{ mb: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                  {supplement.description}
                                </Typography>
                                <Typography variant="caption">{formatDate(supplement.date)}</Typography>
                              </Box>
                            ))}
                            {horse.food_supplement_history.length > 2 && (
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                +{horse.food_supplement_history.length - 2} autres
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      {userRole === "admin" && (
                        <TableCell align="center" sx={{ border: "1px solid #ddd" }}>
                          <Tooltip title="Modifier">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditHorse(horse)}
                              sx={{ color: "#003366" }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Supprimer">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteHorse(horse.id)}
                              sx={{ color: "#d32f2f" }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add/Edit Horse Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ backgroundColor: "#003366", color: "white" }}>
          {isEditing ? "Modifier les détails du cheval" : "Ajouter un nouveau cheval"}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1 }}>
            {/* Basic Information */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="Nom du cheval"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                sx={{ flexGrow: 1, minWidth: "200px" }}
              />
              {userRole === "admin" ? (
                <FormControl sx={{ flexGrow: 1, minWidth: "200px" }}>
                  <InputLabel>Propriétaire</InputLabel>
                  <Select
                    name="user_id"
                    value={formData.user_id}
                    onChange={handleInputChange}
                    label="Propriétaire"
                    required
                  >
                    {owners.map((owner) => (
                      <MenuItem key={owner.id} value={owner.id}>
                        {owner.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  label="Propriétaire"
                  name="owner"
                  value={formData.owner}
                  onChange={handleInputChange}
                  required
                  sx={{ flexGrow: 1, minWidth: "200px" }}
                  disabled
                />
              )}
              <TextField
                label="Groom concerné"
                name="groom"
                value={formData.groom}
                onChange={handleInputChange}
                required
                sx={{ flexGrow: 1, minWidth: "200px" }}
              />
            </Box>

            {/* Physical Details */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <FormControl sx={{ minWidth: "150px" }}>
                <InputLabel>Type de pension</InputLabel>
                <Select
                  name="pension_type"
                  value={formData.pension_type}
                  onChange={handleInputChange}
                  label="Type de pension"
                >
                  <MenuItem value="1">Pension 1 (600 DTS)</MenuItem>
                  <MenuItem value="2">Pension 2 (750 DTS)</MenuItem>
                </Select>
              </FormControl>

              {/* Measurement fields for weight calculation */}
              <TextField
                label="Hauteur au garrot (HG) en cm"
                name="hg"
                type="number"
                value={formData.hg}
                onChange={handleInputChange}
                sx={{ minWidth: "200px" }}
              />
              <TextField
                label="Périmètre thoracique (PT) en cm"
                name="pt"
                type="number"
                value={formData.pt}
                onChange={handleInputChange}
                sx={{ minWidth: "200px" }}
              />
              <TextField
                label="Poids en KG"
                name="weight"
                type="number"
                value={formData.weight}
                onChange={handleInputChange}
                sx={{ minWidth: "150px" }}
                helperText="Peut être calculé automatiquement ou saisi directement"
              />
              <TextField
                label="UFC"
                name="ufc"
                type="number"
                value={formData.ufc}
                onChange={handleInputChange}
                required
                sx={{ minWidth: "150px" }}
              />
            </Box>

            {/* Health Information */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="Dernière vermifuge"
                type="date"
                name="last_deworming"
                value={formData.last_deworming}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: "200px" }}
              />
              <TextField
                label="Dernière visite maréchal ferrant"
                type="date"
                name="last_farrier_visit"
                value={formData.last_farrier_visit}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: "200px" }}
              />
              <TextField
                label="Nom du maréchal ferrant"
                name="marichal_name"
                value={formData.marichal_name}
                onChange={handleInputChange}
                sx={{ flexGrow: 1, minWidth: "200px" }}
              />
            </Box>

            {/* Vaccine Information */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                border: "1px solid #e0e0e0",
                p: 2,
                borderRadius: "4px",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Dernier Vaccin
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label="Type de vaccin"
                  name="vaccine_type"
                  value={formData.vaccine_type}
                  onChange={handleInputChange}
                  required
                  sx={{ flexGrow: 1, minWidth: "200px" }}
                />
                <TextField
                  label="Date du vaccin"
                  type="date"
                  name="vaccine_date"
                  value={formData.vaccine_date}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: "200px" }}
                />
                <TextField
                  label="Réalisé par"
                  name="vaccine_doctor"
                  value={formData.vaccine_doctor}
                  onChange={handleInputChange}
                  sx={{ flexGrow: 1, minWidth: "200px" }}
                />
              </Box>
            </Box>

            {/* Description */}
            <TextField
              label="Description du cheval"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={3}
              sx={{ width: "100%" }}
              helperText="Description générale du cheval, notes particulières, etc."
            />

            {/* Bedding History */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                border: "1px solid #e0e0e0",
                p: 2,
                borderRadius: "4px",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  Historique des Suppléments de Litière
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addBeddingEntry}
                  variant="outlined"
                  size="small"
                  sx={{ color: "#003366", borderColor: "#003366" }}
                >
                  Ajouter
                </Button>
              </Box>
              {formData.bedding_history.length === 0 ? (
                <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                  Aucun historique de litière. Cliquez sur "Ajouter" pour commencer.
                </Typography>
              ) : (
                <List sx={{ maxHeight: 200, overflow: "auto" }}>
                  {formData.bedding_history.map((bedding, index) => (
                    <ListItem key={index} sx={{ border: "1px solid #e0e0e0", mb: 1, borderRadius: 1 }}>
                      <Box sx={{ display: "flex", gap: 2, width: "100%", alignItems: "center" }}>
                        <FormControl sx={{ minWidth: "200px" }}>
                          <InputLabel>Type de litière</InputLabel>
                          <Select
                            value={bedding.bedding_type}
                            onChange={(e) => updateBeddingEntry(index, "bedding_type", e.target.value)}
                            label="Type de litière"
                            size="small"
                          >
                            <MenuItem value="Paille">Paille</MenuItem>
                            <MenuItem value="Copeaux de bois">Copeaux de bois</MenuItem>
                            <MenuItem value="Autre">Autre</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          label="Date"
                          type="date"
                          value={bedding.date}
                          onChange={(e) => updateBeddingEntry(index, "date", e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          sx={{ minWidth: "150px" }}
                        />
                        <IconButton onClick={() => removeBeddingEntry(index)} color="error" size="small">
                          <RemoveIcon />
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            {/* Food Supplement History */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                border: "1px solid #e0e0e0",
                p: 2,
                borderRadius: "4px",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  Historique des Suppléments Alimentaires/Vitamines
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addFoodSupplementEntry}
                  variant="outlined"
                  size="small"
                  sx={{ color: "#003366", borderColor: "#003366" }}
                >
                  Ajouter
                </Button>
              </Box>
              {formData.food_supplement_history.length === 0 ? (
                <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                  Aucun historique de suppléments. Cliquez sur "Ajouter" pour commencer.
                </Typography>
              ) : (
                <List sx={{ maxHeight: 200, overflow: "auto" }}>
                  {formData.food_supplement_history.map((supplement, index) => (
                    <ListItem key={index} sx={{ border: "1px solid #e0e0e0", mb: 1, borderRadius: 1 }}>
                      <Box sx={{ display: "flex", gap: 2, width: "100%", alignItems: "center" }}>
                        <TextField
                          label="Description"
                          value={supplement.description}
                          onChange={(e) => updateFoodSupplementEntry(index, "description", e.target.value)}
                          multiline
                          rows={2}
                          size="small"
                          sx={{ flexGrow: 1, minWidth: "250px" }}
                        />
                        <TextField
                          label="Date"
                          type="date"
                          value={supplement.date}
                          onChange={(e) => updateFoodSupplementEntry(index, "date", e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          sx={{ minWidth: "150px" }}
                        />
                        <IconButton onClick={() => removeFoodSupplementEntry(index)} color="error" size="small">
                          <RemoveIcon />
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ backgroundColor: "#003366", "&:hover": { backgroundColor: "#002244" } }}
          >
            {isEditing ? "Mettre à jour" : "Ajouter"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDeleteHorse}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title" sx={{ backgroundColor: "#003366", color: "white" }}>
          Confirmation de suppression
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1">
            Êtes-vous sûr de vouloir supprimer ce cheval? Cette action ne peut pas être annulée.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteHorse} color="inherit">
            Annuler
          </Button>
          <Button
            onClick={confirmDeleteHorse}
            variant="contained"
            color="error"
            sx={{ backgroundColor: "#d32f2f", "&:hover": { backgroundColor: "#b71c1c" } }}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
